// Verbatim copy used by the trivia results UI. Centralized here so that
// ticket-mandated strings live in exactly one place and cannot drift between
// authenticated / public render paths.

export const LINEAR_SECTION_EYEBROW = "RATING DISTRIBUTION";

export const RATINGS_HIDDEN_NOTE =
  "Ratings with no responses are hidden to reduce noise.";

export const buildLinearCoverageCount = (answered, total) =>
  `${answered} of ${total} ratings received responses`;

export const buildResponseCountLabel = (n) =>
  n === 1 ? "1 response" : `${n} responses`;

export const SENTIMENT_LABEL_POSITIVE = "Positive";
export const SENTIMENT_LABEL_NEUTRAL = "Neutral";
export const SENTIMENT_LABEL_NEGATIVE = "Negative";

// Internal key — used for data lookups and comparisons that expect the
// no-space form (e.g. the Survey response map keyed by "Neutral/Mixed").
export const SENTIMENT_LABEL_NEUTRAL_MIXED = "Neutral/Mixed";

// Display form — used only for UI render (bar label, modal title). The ticket
// target uses spaces around the slash; kept separate from the internal key so
// the display tweak can't break Survey data lookups.
export const SENTIMENT_LABEL_NEUTRAL_MIXED_DISPLAY = "Neutral / Mixed";

// First-column header of the sentiment-responses modal. Used by the Survey
// path only; messaging trivvys drop the column entirely (message is identical
// across all rows and can be long — just Comment + %Sentiment).
export const SENTIMENT_MODAL_COLUMN_QUESTION = "Question";
export const SENTIMENT_MODAL_COLUMN_MESSAGE = "Message";
// Message Insights card — action button labels. Centralized so the live UI
// render site and the hidden PDF-export render site stay in sync and the copy
// lives in one place. The Get/Hide pair drives the collapsible body toggle,
// matching the survey "By Question" card behavior.
export const GENERATE_INSIGHTS_LABEL = "Generate Insights";

export const REGENERATE_INSIGHTS_LABEL = "Regenerate Insights";

export const GET_FULL_INSIGHTS_LABEL = "Get Full Insights";

export const HIDE_FULL_INSIGHTS_LABEL = "Hide Full Insights";
// Shown in the "Total Duration" column / CSV and the "Avg Time" card when a
// take has no trustworthy duration (text/SMS takes, which record a bogus
// timeTaken). Value matches the adjacent Score cell's "N/A" so the table reads
// consistently.
export const TEXT_TAKE_DURATION_NA = "N/A";

// Per-table edit gate on the Results insights category tables ("Key Findings"
// and "Suggested Next Steps"). The pencil flips a single table between a
// read-only view and the editable textareas; centralized so the label/tooltip
// copy lives in exactly one place.
export const EDIT_INSIGHTS_LABEL = "Edit";

export const DONE_EDIT_INSIGHTS_LABEL = "Done";

export const EDIT_INSIGHTS_ARIA = "Edit this table's content";

// Discoverability hint shown above the Key Findings / Suggested Next Steps
// tabs. Sharing is a select-then-Share flow (tick a checkbox, then click the
// Share button) — the copy describes that rather than implying the item itself
// is click-to-share. Sentence case (only the first letter uppercase) per the
// design note; the render site disables the section's small-caps styling.
export const INSIGHTS_SHARE_HINT =
  "Select one or more findings or next steps, then click share to send them to your users.";

// Hashtag Insights (bug903) — centralized so this copy has one home.
export const buildHashtagInsightsDisabledReason = (eligibleCount, minRequired) =>
  `Needs at least ${minRequired} messages in this range to generate insights (${eligibleCount} found).`;

export const HASHTAG_INSIGHTS_ZERO_IN_RANGE =
  "No eligible messages in this date range yet.";

export const HASHTAG_INSIGHTS_GENERATION_FAILED =
  "We couldn't generate insights for this hashtag. Please try again.";

export const HASHTAG_INSIGHTS_CHECKING_ELIGIBILITY =
  "Checking message count for this range...";

// Hashtag PDF export (bug915) — chat + insights downloads share this copy.
export const HASHTAG_PDF_DOWNLOAD_LABEL = "Download";

export const HASHTAG_PDF_GENERATING_LABEL = "Generating PDF...";

export const HASHTAG_PDF_CHAT_LABEL = "Chat";

export const HASHTAG_PDF_INSIGHTS_LABEL = "Insights";

export const HASHTAG_PDF_ATTACHMENT_LABEL = "Attachment";

export const HASHTAG_PDF_EXPORTED_PREFIX = "Exported";

export const buildHashtagPdfMessageCount = (n) =>
  n === 1 ? "1 message" : `${n} messages`;

export const buildHashtagPdfAttachmentLine = (fileName) =>
  fileName && fileName !== HASHTAG_PDF_ATTACHMENT_LABEL
    ? `${HASHTAG_PDF_ATTACHMENT_LABEL}: ${fileName}`
    : HASHTAG_PDF_ATTACHMENT_LABEL;

export const HASHTAG_CHAT_PDF_FAILED =
  "We couldn't create the chat PDF. Please try again.";

export const HASHTAG_INSIGHTS_PDF_FAILED =
  "We couldn't create the insights PDF. Please try again.";

// Results > By User "Translate" control (bug939). Copy lives here so both By
// User views (Messages and Survey/KC) share it.
export const TRANSLATE_LABEL = "Translate";

export const TRANSLATE_OPTION_ORIGINAL = "Original";

export const TRANSLATE_OPTION_ENGLISH = "English";

export const buildTranslateProgress = (done, total) => {
  if (total) {
    return `Translating... ${done} of ${total} ${total === 1 ? "response" : "responses"}`;
  }
  if (done > 0) {
    return `Translating... ${buildResponseCountLabel(done)} checked`;
  }
  return "Translating...";
};

export const buildTranslateDone = (n) => `${buildResponseCountLabel(n)} translated`;

export const buildTranslateFailed = (n) =>
  n === 1
    ? "1 response couldn't be translated."
    : `${n} responses couldn't be translated.`;

// Answers another admin's request is still translating; never reported as done.
export const buildTranslatePending = (n) =>
  n === 1
    ? "1 response is still being translated."
    : `${n} responses are still being translated.`;

export const TRANSLATE_MESSAGE_NOTHING = "No responses needed translation.";

// Done line: overall translated total, then the failed and pending counts as their own sentences.
export const buildTranslateSummary = (translated, failed, pending) => {
  const sentences = [];
  if (translated > 0 && (failed > 0 || pending > 0)) {
    sentences.push(`${buildTranslateDone(translated)}.`);
  } else if (translated > 0) {
    sentences.push(buildTranslateDone(translated));
  }
  if (failed > 0) {
    sentences.push(buildTranslateFailed(failed));
  }
  if (pending > 0) {
    sentences.push(buildTranslatePending(pending));
  }
  if (sentences.length === 0) {
    return TRANSLATE_MESSAGE_NOTHING;
  }
  return sentences.join(" ");
};

export const TRANSLATE_MESSAGE_BUSY =
  "Translation is already in progress. Retrying...";

export const TRANSLATE_MESSAGE_RATE_LIMITED = "Translation is busy. Retrying...";

export const TRANSLATE_MESSAGE_ERROR =
  "Couldn't translate right now. Showing the original text.";

export const TRANSLATE_RETRY_LABEL = "Retry";

export const TRANSLATE_MESSAGE_UNAVAILABLE =
  "Translation isn't available yet. Showing the original text.";

export const TRANSLATE_MESSAGE_ANONYMITY =
  "Translation isn't available until there are enough responses to keep them anonymous.";

export const TRANSLATE_MESSAGE_DISABLED =
  "Translation is turned off right now. Showing the original text.";

// Hashtag notification channels (bug932) — picker section copy, verbatim
// from the ticket. The heading renders uppercase through CSS only.
export const NOTIFICATION_CHANNELS_HEADING =
  "How should recipients be notified and reply?";

export const NOTIFICATION_CHANNEL_EMAIL_TITLE = "Email + platform";

export const NOTIFICATION_CHANNEL_EMAIL_DESC =
  "Recipients get an email every time a message arrives and reply in the platform.";

export const NOTIFICATION_CHANNEL_SMS_TITLE = "Text message (SMS)";

export const NOTIFICATION_CHANNEL_SMS_DESC =
  "Recipients get messages by text and reply by text. The whole conversation happens over SMS, with no platform login needed.";
